import { lazyWithRetry as lazy, useMediaQuery } from "@src/utils/CommonUtils";
import AppLayout from "@src/pages/layout/AppLayout";
import ProtectedRoute from "@src/routes/ProtectedRoute";
import { NavbarRoutes } from "@src/routes/NavbarRoutes";
import LoginLayout from "@src/components/login-modal/LoginLayout";
import ThemeWrapper from "@src/pages/challenge-landing/ThemeWrapper";
import LazyWrapper from "@src/components/lazy-wrapper/LazyWrapper";
import { isElectronApp, isNewLandingEnabled } from "@src/utils/envUtils";
import { Navigate, useParams, useLocation } from "react-router-dom";
import Login from "@src/pages/login/Login";
import TermsAndConditions from "@src/pages/login/terms-and-conditions/TermsAndConditions";

import JoinGroupHandler from "@src/pages/student/quiz-group/join-quiz-group/JoinGroupHandler";
import JoinQuizHandler from "@src/pages/student/my-quiz/join-quiz/JoinQuizHandler";

const PlacementRedirect = () => {
  const location = useLocation();
  const target = location.pathname.replace(/^\/placement(\/|$)/, "/placements$1") + location.search + location.hash;
  return <Navigate to={target} replace />;
};

const QuizAssignRedirect = () => {
  const { quizId } = useParams();
  return <Navigate to={`/quiz/assign/${quizId}`} replace />;
};

const QuizInviteRedirect = () => {
  const { orgId } = useParams();
  return <Navigate to={orgId ? `/quiz/invite/${orgId}` : "/quiz/invite"} replace />;
};

// Lazy-loaded page components
const PageNotFound = lazy(
  () => import("@src/pages/page-not-found/PageNotFound"),
);
const ActivationPage = lazy(
  () => import("@src/pages/login/activation-page/ActivationPage"),
);
const ChooseUserRole = lazy(
  () => import("@src/pages/student/choose-organization/ChooseUserRole"),
);
const Multi_roles = lazy(
  () => import("@src/pages/login/multi-roles/MultiRoles"),
);
const OrganizationDetailsList = lazy(
  () => import("@src/pages/OrganizationDetailsList"),
);
const ChallengeLanding = lazy(
  () => import("@src/pages/challenge-landing/ChallengeLanding"),
);
const LandingWebsite = lazy(
  () => import("@src/pages/landing-page/LandingWebsite"),
);
const LandingElectron = lazy(
  () => import("@src/pages/landing-page/LandingElectron"),
);
const LandingPortal = lazy(
  () => import("@src/pages/landing-portal/LandingPortal"),
);
const ElectronLandingWebsite = lazy(
  () => import("@src/pages/electron-landing-page/ElectronLandingWebsite"),
);
const TopicCompaniesDetails = lazy(
  () =>
    import("@src/pages/challenge-landing/topic-companies-details/TopicCompaniesDetails"),
);
const ChallengeListDetails = lazy(
  () =>
    import("@src/pages/challenge-landing/challenge-list/ChallengeListDetails"),
);
const ChallengeExamScreen = lazy(
  () => import("@src/pages/challenge-exam/ChallengeExamScreen"),
);
const PlacementLayout = lazy(
  () => import("@src/pages/placement/PlacementLayout"),
);
import { placementRoutes } from "@src/pages/placement/placementRoutes";

const StudentTournamentsList = lazy(
  () =>
    import("@src/pages/student/tournaments/student-tournaments-list/StudentTournamentsList"),
);
const TournamentLayout = lazy(
  () => import("@src/pages/task-manager/tournaments/TournamentLayout"),
);
const CompletedTournamentQuestionList = lazy(
  () =>
    import("@src/pages/task-manager/tournaments/completed-tournament-questions/CompletedTournamentQuestionList"),
);
const QuestionDetails = lazy(
  () =>
    import("@src/pages/task-manager/tournaments/completed-tournament-questions/QuestionDetails"),
);
const CompletedTournamentDetailsList = lazy(
  () =>
    import("@src/pages/task-manager/tournaments/completed-tournament-questions/CompletedTournamentDetailsList"),
);
const InstructionLayout = lazy(
  () => import("@src/pages/student/instructions/InstructionLayout"),
);
const StudentLeaderBoardWebsite = lazy(
  () =>
    import("@src/pages/student/tournaments/student-leader-board/StudentLeaderBoardWebsite"),
);
const MyQuizList = lazy(
  () => import("@src/pages/student/my-quiz/my-quiz-list/MyQuizList"),
);
const ResultSummary = lazy(
  () => import("@src/pages/student/student-evaluation/ResultSummary"),
);
const ResultDetails = lazy(
  () => import("@src/pages/student/student-evaluation/ResultDetails"),
);
const WebsiteQuiz = lazy(() => import("@src/pages/websit-quiz/WebsiteQuiz"));
const WebsiteTournament = lazy(
  () => import("@src/pages/website-tournament/WebsiteTournament"),
);
const MyOrganization = lazy(
  () => import("@src/components/student-org-card/MyOrganization"),
);
const InvitePeopleForQuizHandler = lazy(
  () =>
    import("@src/pages/student/my-quiz/invite-people/InivitePeopleForQuizHandler"),
);
const ProfileLanding = lazy(() =>
  import("@src/pages/profile-summary/ProfileLanding").then((m) => ({
    default: m.ProfileLanding,
  })),
);
const MobileChallengeExamScreen = lazy(
  () => import("@src/pages/challenge-exam/MobileChallengeExamScreen"),
);
const SubjectsLearningComponent = lazy(
  () =>
    import("@src/pages/subjects-learning-component/SubjectsLearningComponent"),
);
const ZupflyLayout = lazy(() => import("@src/pages/layout/ZupflyLayout"));
const ZupflyBatchDetail = lazy(
  () => import("@src/pages/zupfly/batch-detail/ZupflyBatchDetail"),
);
const ZupflyQuizList = lazy(
  () => import("@src/pages/zupfly/quiz-list/ZupflyQuizList"),
);
const WordzappStudentHome = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentHome"),
);
const WordzappStudentListsView = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentListsView"),
);
const WordzappStudentLearn = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentLearn"),
);
const WordzappStudentFlashcards = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentFlashcards"),
);
const WordzappStudentFlashcardsMarked = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentFlashcardsMarked"),
);
const WordzappStudentWordBank = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentWordBank"),
);
const WordzappStudentMockQuiz = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentMockQuiz"),
);
const WordzappStudentMockTestRunner = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentMockTestRunner"),
);
const WordzappStudentGlobalDictionary = lazy(
  () => import("@src/pages/zupfly/wordzapp/WordzappStudentGlobalDictionary"),
);
const ExamLayoutScreen = lazy(
  () => import("@src/pages/student/exam-layout/ExamLayoutScreen"),
);

const ResponsiveChallengeExam = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <ThemeWrapper>
      <LazyWrapper>
        {isMobile ? <MobileChallengeExamScreen /> : <ChallengeExamScreen />}
      </LazyWrapper>
    </ThemeWrapper>
  );
};

const LandingRoute = () => {
  const isAuthenticated = !!localStorage.getItem("accessToken");
  const isElectron = isElectronApp();

  if (isElectron) {
    return isAuthenticated ? (
      <LazyWrapper>
        <LandingElectron />
      </LazyWrapper>
    ) : (
      <Navigate to="/login" replace />
    );
  }
  return isAuthenticated ? (
    <LazyWrapper>
      <LandingWebsite />
    </LazyWrapper>
  ) : (
    <Navigate to="/login" replace />
  );
};

const LoginRoute = () => {
  const isAuthenticated = !!localStorage.getItem("accessToken");
  return isAuthenticated ? <Navigate to="/learn" replace /> : <Login />;
};

const electronRoutes = [
  {
    path: "/",
    element: <LoginLayout />,
    children: [
      {
        index: true,
        element: <LandingRoute />,
      },
      {
        path: "login",
        element: <LoginRoute />,
      },
      {
        path: "learn",
        element: (
          <LazyWrapper>
            <LandingElectron />
          </LazyWrapper>
        ),
      },
      {
        path: "terms-and-conditions",
        element: <TermsAndConditions />,
      },
      {
        path: "signup",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "learn-subjects",
        element: (
          <LazyWrapper>
            <SubjectsLearningComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "multi_roles",
        element: (
          <LazyWrapper>
            <Multi_roles />
          </LazyWrapper>
        ),
      },
      {
        path: "choose-role",
        element: (
          <LazyWrapper>
            <ChooseUserRole />
          </LazyWrapper>
        ),
      },
      {
        path: "landing-portal",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <LandingPortal />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "portal",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <LandingPortal />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "registration-success",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "change-password",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "password-success",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/link-expired",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/set-password-link-expired",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/reset-password-link-expired",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/resent-password-link",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "set-password/:token",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/reset-password/:token",
        element: (
          <LazyWrapper>
            <ElectronLandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/profile",
        element: (
          <LazyWrapper>
            <ProfileLanding />
          </LazyWrapper>
        ),
      },
      {
        path: "activation-student/:token",
        element: (
          <LazyWrapper>
            <ActivationPage />
          </LazyWrapper>
        ),
      },
      {
        path: "/",
        element: <ProtectedRoute />,
        children: [
          {
            path: "zupfly",
            element: (
              <LazyWrapper>
                <ZupflyLayout />
              </LazyWrapper>
            ),
            children: [
              {
                path: "batch-detail",
                element: (
                  <LazyWrapper>
                    <ZupflyBatchDetail />
                  </LazyWrapper>
                ),
              },
              {
                path: "wordzapp",
                element: (
                  <LazyWrapper>
                    <WordzappStudentHome />
                  </LazyWrapper>
                ),
              },
              {
                path: "wordzapp/lists",
                element: (
                  <LazyWrapper>
                    <WordzappStudentListsView />
                  </LazyWrapper>
                ),
              },
              {
                path: "wordzapp/learn",
                element: (
                  <LazyWrapper>
                    <WordzappStudentLearn />
                  </LazyWrapper>
                ),
              },
              {
                path: "wordzapp/flashcards",
                element: (
                  <LazyWrapper>
                    <WordzappStudentFlashcards />
                  </LazyWrapper>
                ),
              },
              {
                path: "wordzapp/flashcards/marked",
                element: (
                  <LazyWrapper>
                    <WordzappStudentFlashcardsMarked />
                  </LazyWrapper>
                ),
              },
              {
                path: "wordzapp/word-bank",
                element: (
                  <LazyWrapper>
                    <WordzappStudentWordBank />
                  </LazyWrapper>
                ),
              },
              {
                path: "quizzes",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <ZupflyQuizList />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "instruction",
                    element: (
                      <LazyWrapper>
                        <InstructionLayout type="quiz" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "quiz-screen",
                    element: (
                      <LazyWrapper>
                        <ExamLayoutScreen />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "details",
                    element: (
                      <LazyWrapper>
                        <ResultDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "",
            element: <AppLayout />,
            children: [...NavbarRoutes],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: "/placements",
            element: (
              <LazyWrapper>
                <PlacementLayout />
              </LazyWrapper>
            ),
            children: placementRoutes,
          },
          {
            path: "/placement/*",
            element: <PlacementRedirect />,
          },
          {
            path: "/placement",
            element: <Navigate to="/placements" replace />,
          },
        ],
      },
      {
        path: "*",
        element: (
          <div>
            <LazyWrapper>
              <PageNotFound />
            </LazyWrapper>
          </div>
        ),
      },
    ],
  },
];

const WebLandingRoute = () => {
  const isAuthenticated = !!localStorage.getItem("accessToken");
  const useNewLanding = isNewLandingEnabled();

  if (useNewLanding && isAuthenticated) {
    return <Navigate to="/landing-portal" replace />;
  }

  return (
    <ThemeWrapper>
      <LazyWrapper>
        <ChallengeLanding />
      </LazyWrapper>
    </ThemeWrapper>
  );
};

const WebHomeRoute = () => {
  const isAuthenticated = !!localStorage.getItem("accessToken");
  const useNewLanding = isNewLandingEnabled();

  if (useNewLanding && isAuthenticated) {
    return <Navigate to="/landing-portal" replace />;
  }

  return (
    <ThemeWrapper>
      <LazyWrapper>
        <LandingWebsite />
      </LazyWrapper>
    </ThemeWrapper>
  );
};

const webRoutes = [
  {
    path: "/",
    element: <LoginLayout />,
    children: [
      {
        path: "",
        element: <WebLandingRoute />,
      },
      {
        path: "home",
        element: <WebHomeRoute />,
      },
      {
        path: "landing-portal",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <LandingPortal />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "portal",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <LandingPortal />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "login",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <LandingWebsite />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "website-quiz",
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <MyQuizList type="website-quiz" />
              </LazyWrapper>
            ),
          },
          {
            path: "instruction",
            element: (
              <LazyWrapper>
                <InstructionLayout type="website-quiz" />
              </LazyWrapper>
            ),
          },
          {
            path: "quiz-screen",
            element: (
              <div
                style={{ height: "100vh", background: "var(--base-bg-color)" }}
              >
                <LazyWrapper>
                  <WebsiteQuiz />
                </LazyWrapper>
              </div>
            ),
          },
          {
            path: ":quiz-screen/result",
            element: (
              <LazyWrapper>
                <ResultSummary />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            element: (
              <LazyWrapper>
                <ResultDetails />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "website-tournaments",
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentTournamentsList type="website-tournament" />
              </LazyWrapper>
            ),
          },
          {
            path: "tournament-screen",
            element: (
              <div
                style={{ height: "100vh", background: "var(--base-bg-color)" }}
              >
                <LazyWrapper>
                  <WebsiteTournament />
                </LazyWrapper>
              </div>
            ),
          },
          {
            path: "tournament-screen/leaderBoard",
            element: (
              <LazyWrapper>
                <StudentLeaderBoardWebsite />
              </LazyWrapper>
            ),
          },
          {
            path: "tournament-screen/tournamentResult",
            element: (
              <LazyWrapper>
                <TournamentLayout />
              </LazyWrapper>
            ),
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <CompletedTournamentQuestionList />
                  </LazyWrapper>
                ),
              },
              {
                path: "questionDetails",
                element: (
                  <LazyWrapper>
                    <QuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
          {
            path: "tournamentDetails",
            element: (
              <LazyWrapper>
                <TournamentLayout />
              </LazyWrapper>
            ),
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <CompletedTournamentDetailsList />
                  </LazyWrapper>
                ),
              },
              {
                path: "questionDetails",
                element: (
                  <LazyWrapper>
                    <QuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
          {
            path: "tournament-instruction",
            element: (
              <LazyWrapper>
                <InstructionLayout type="website-tournament" />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "/challenges/:name",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <ChallengeLanding />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "/challenges/:name/list",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <ChallengeListDetails />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "challenges/:name/topic",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <TopicCompaniesDetails />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "challenges/:name/question",
        element: <ResponsiveChallengeExam />,
      },
      {
        path: "/shared-question/:questionId",
        element: <ResponsiveChallengeExam />,
      },
      {
        path: "challenges/:name/company",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <TopicCompaniesDetails />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "challenges/:name/:name/:title",
        element: <ResponsiveChallengeExam />,
      },
      {
        path: "/student-organization",
        element: (
          <ThemeWrapper>
            <LazyWrapper>
              <MyOrganization />
            </LazyWrapper>
          </ThemeWrapper>
        ),
      },
      {
        path: "signup",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "registration-success",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "change-password",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "password-success",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/link-expired",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/set-password-link-expired",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/reset-password-link-expired",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/resent-password-link",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "set-password/:token",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/reset-password/:token",
        element: (
          <LazyWrapper>
            <LandingWebsite />
          </LazyWrapper>
        ),
      },
      {
        path: "/student-organization",
        element: (
          <LazyWrapper>
            <OrganizationDetailsList />
          </LazyWrapper>
        ),
      },
      {
        path: "/profile",
        element: (
          <LazyWrapper>
            <ProfileLanding />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/join-group/:groupId",
    element: <JoinGroupHandler />,
  },
  {
    path: "/quiz/assign/:quizId",
    element: <JoinQuizHandler />,
  },
  {
    path: "/v1/quiz/assign/:quizId",
    element: <QuizAssignRedirect />,
  },
  {
    path: "/quiz/invite",
    element: (
      <LazyWrapper>
        <InvitePeopleForQuizHandler />
      </LazyWrapper>
    ),
  },
  {
    path: "/quiz/invite/:orgId",
    element: (
      <LazyWrapper>
        <InvitePeopleForQuizHandler />
      </LazyWrapper>
    ),
  },
  {
    path: "/v1/quiz/invite",
    element: <QuizInviteRedirect />,
  },
  {
    path: "/v1/quiz/invite/:orgId",
    element: <QuizInviteRedirect />,
  },
  {
    path: "/landing-portal",
    element: (
      <ThemeWrapper>
        <LazyWrapper>
          <LandingPortal />
        </LazyWrapper>
      </ThemeWrapper>
    ),
  },
  {
    path: "/portal",
    element: (
      <ThemeWrapper>
        <LazyWrapper>
          <LandingPortal />
        </LazyWrapper>
      </ThemeWrapper>
    ),
  },
  {
    path: "/choose-role",
    element: (
      <LazyWrapper>
        <ChooseUserRole />
      </LazyWrapper>
    ),
  },
  {
    path: "/multi_roles",
    element: (
      <LazyWrapper>
        <Multi_roles />
      </LazyWrapper>
    ),
  },
  {
    path: "activation-student/:token",
    element: (
      <LazyWrapper>
        <ActivationPage />
      </LazyWrapper>
    ),
  },
  {
    path: "/",
    element: <ProtectedRoute />,
    children: [
      {
        path: "zupfly",
        element: (
          <LazyWrapper>
            <ZupflyLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "batch-detail",
            element: (
              <LazyWrapper>
                <ZupflyBatchDetail />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp",
            element: (
              <LazyWrapper>
                <WordzappStudentHome />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/lists",
            element: (
              <LazyWrapper>
                <WordzappStudentListsView />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/learn",
            element: (
              <LazyWrapper>
                <WordzappStudentLearn />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/flashcards",
            element: (
              <LazyWrapper>
                <WordzappStudentFlashcards />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/flashcards/marked",
            element: (
              <LazyWrapper>
                <WordzappStudentFlashcardsMarked />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/word-bank",
            element: (
              <LazyWrapper>
                <WordzappStudentWordBank />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/mock-quiz",
            element: (
              <LazyWrapper>
                <WordzappStudentMockQuiz />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/mock-test",
            element: (
              <LazyWrapper>
                <WordzappStudentMockTestRunner />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/mock-quiz/play",
            element: (
              <LazyWrapper>
                <WordzappStudentMockTestRunner />
              </LazyWrapper>
            ),
          },
          {
            path: "wordzapp/dictionary",
            element: (
              <LazyWrapper>
                <WordzappStudentGlobalDictionary />
              </LazyWrapper>
            ),
          },
          {
            path: "test/instructions",
            element: (
              <LazyWrapper>
                <InstructionLayout type="assessment" />
              </LazyWrapper>
            ),
          },
          {
            path: "test/details",
            element: (
              <LazyWrapper>
                <ResultDetails />
              </LazyWrapper>
            ),
          },
          {
            path: "quizzes",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <ZupflyQuizList />
                  </LazyWrapper>
                ),
              },
              {
                path: "instruction",
                element: (
                  <LazyWrapper>
                    <InstructionLayout type="quiz" />
                  </LazyWrapper>
                ),
              },
              {
                path: "quiz-screen",
                element: (
                  <LazyWrapper>
                    <ExamLayoutScreen />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                element: (
                  <LazyWrapper>
                    <ResultDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "",
        element: <AppLayout />,
        children: [...NavbarRoutes],
      },
    ],
  },
  {
    path: "/challenges",
    element: (
      <ThemeWrapper>
        <LazyWrapper>
          <ChallengeLanding />
        </LazyWrapper>
      </ThemeWrapper>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: "/placements",
        element: (
          <LazyWrapper>
            <PlacementLayout />
          </LazyWrapper>
        ),
        children: placementRoutes,
      },
      {
        path: "/placement/*",
        element: <PlacementRedirect />,
      },
      {
        path: "/placement",
        element: <Navigate to="/placements" replace />,
      },
    ],
  },
  {
    path: "*",
    element: (
      <div>
        <LazyWrapper>
          <PageNotFound />
        </LazyWrapper>
      </div>
    ),
  },
];

export const RootRoutes = isElectronApp() ? electronRoutes : webRoutes;

export default RootRoutes;
